// Core
import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarRef as MatSnackBarRef,
} from '@angular/material/legacy-snack-bar';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, finalize, skip, takeUntil, tap } from 'rxjs/operators';

// Components
import { ResponseFormatConstant } from '@app/core/_constant/response-format.constant';
import { IRfqLot } from 'v2/modules/rfq/interfaces/rfq-lot.interface';
import { EOrderTypeValue } from 'v2/modules/sales-order/configs/sales-order.config';
import {
  trackByControlValue,
  trackById,
} from 'v2/modules/shared/util/track-by.util';

import { ILocationOld } from '../../../location/interfaces/location.interface';
import { LocationFacadeService } from '../../../location/location-facade.service';
import { IPurchaseOrderShipment } from '../../../purchase-order/interfaces/purchase-order.interface';
import { PurchaseOrderFacadeService } from '../../../purchase-order/purchase-order-facade.service';
import { RfqLotFormComponent } from '../../../rfq/components/rfq-lot-form/rfq-lot-form.component';

// Configs
import { IWarehouse } from '../../../shared';
import { DialogConfig } from '../../../shared/configs/dialog.config';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';

// Interfaces
import { SupplierApiService } from '../../../shared/services/api/supplier/supplier-api.service';
import {
  atLeastOneArrayItemValidator,
  requiredNonZeroValidator,
} from '../../../shared/util/custom-validation.utils';
import { ISupplierSku } from '../../../supplier/interfaces/supplier-sku.interface';
import { TagFacadeService } from '../../../tag/tag-facade.service';
import {
  EReceivingReportFilterBy,
  IReceivableOrder,
  OrderItem,
} from '../../interfaces/receiving-report.interface';

// Services
import { ReceivingFacadeService } from '../../receiving-facade.service';

import { SettingsService } from './../../../../../app/core/settings/services/settings.service';
import { FeatureToggleService } from './../../../../../app/shared/services/feature-toggle.service';

// Utilities or shared
import { CostAllocationTypeConstants, EExtraCosts } from '@app/core/_constant/settings.constants';

@Component({
  selector: 'lum-receiving-report-unified-form',
  templateUrl: './receiving-report-unified-form.component.html',
  styleUrls: ['./receiving-report-unified-form.component.scss'],
})
export class ReceivingReportUnifiedFormComponent implements OnInit, OnDestroy {
  @ViewChild('scrollingContainer', { static: true })
  scrollingContainer! :ElementRef;

  form :UntypedFormGroup;
  allowRefecthLocations :boolean = true;
  recevingReportFilterBy = EReceivingReportFilterBy;
  currentSearchFilter :EReceivingReportFilterBy =
    EReceivingReportFilterBy.PURCHASE_ORDER_ID;
  receivableOrders :IReceivableOrder[] = [];
  shipmentRelations = [
    'shipmentItems.supplierPurchaseOrderItem.stock.warehouse',
    'shipmentItems.supplierPurchaseOrderItem.rfq.defaultReceivingBin',
    'shipmentItems.unitOfMeasure',
    'shipmentExtraCosts',
    'shipmentStatus',
    'warehouse',
    'warehouse.defaultReceivingLocation',
    'shipmentItems.supplierPurchaseOrderItem.supplierPurchaseOrder.internalResource.destinationWarehouse.defaultReceivingLocation',
  ];
  relations = {
    [EReceivingReportFilterBy.PURCHASE_ORDER_ID]:
      'supplierPurchaseOrderItems.baseUnitOfMeasure|supplierPurchaseOrderItems.stock.warehouse.defaultReceivingLocation|supplierPurchaseOrderItems.rfq.defaultReceivingBin|supplierPurchaseOrderItems.orderedUnitOfMeasure|internalResource.destinationWarehouse.defaultReceivingLocation',
    [EReceivingReportFilterBy.SHIPMENT_REFERENCE_NUMBER]:
      this.shipmentRelations.join('|'),
  };

  eOrderTypeValue = EOrderTypeValue;
  ftLocationBins :boolean = false;
  ftLotTracking :boolean = false;

  private onDestroy$ = new Subject<void>();
  trackByControlValue = trackByControlValue;
  trackById = trackById;
  loadingSupplier :boolean = false;
  suppliers :ISupplierSku[] = [];
  suppliersPreviousList :ISupplierSku[] = [];
  supplierIdDisplayValue :string | null = null;
  currentSearchValue :string | null = '';
  private snackbarRef :MatSnackBarRef<any> | null = null;

  shipmentItemsSubject = new BehaviorSubject<AbstractControl[]>([]);
  shipmentItems$ :Observable<AbstractControl[]> =
    this.shipmentItemsSubject.asObservable();

  alternateIdsFormArray: UntypedFormArray;

  constructor(
    private fb :UntypedFormBuilder,
    private purchaseOrderFacadeService :PurchaseOrderFacadeService,
    private locationFacadeService :LocationFacadeService,
    private receivingReportFacadeService :ReceivingFacadeService,
    private tagFacadeService :TagFacadeService,
    private settingService :SettingsService,
    private ft :FeatureToggleService,
    private snackbar :MatSnackBar,
    private dialog :MatDialog,
    private datePipe :DatePipe,
    private dialogRef :MatDialogRef<ReceivingReportUnifiedFormComponent>,
    @Inject(MAT_DIALOG_DATA)
    private data :{
      id :number;
      ftLocationBins ?:boolean;
      supplierPurchaseOrderId ?:number;
      extraCosts ?:any[];
      preloadedLineItems ?:any[];
    },
    private supplierApiService :SupplierApiService,
  ) {}

  ngOnInit() :void {
    this.purchaseOrderFacadeService.setState({ shipments: [] });
    this.receivingReportFacadeService.setState({ receivablesOrder: null });
    this.tagFacadeService
      .getTags({ params: {} })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();

    if (this.currentReceivingReportId) {
      this.loadCurrentReceivingReport();
    }

    if (this.data?.supplierPurchaseOrderId) {
      this.currentSearchValue = this.data.supplierPurchaseOrderId.toString();
      this.receivingReportFacadeService
        .onSearchReceivableOrders({
          searchQuery: this.data.supplierPurchaseOrderId.toString(),
          type: this.currentSearchFilter,
          relations: this.relations[this.currentSearchFilter],
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response) => {
            const order = response?.[0];
            if (order) {
              this.onSelectReceivableOrder(order);
            }
          },
        });
    }

    this.getSuppliers();
    this.initForm();
    this.initData();
    this.getReceivableOrders().subscribe();
    this.initTrackInventoryByLocation();
    this.ftLotTracking = this.ft.featureEnabled('red_DV-1356_lot_tracking');
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSelectReceivableOrder(order :IReceivableOrder) :void {
    if (
      this.currentSearchFilter !==
        EReceivingReportFilterBy.SHIPMENT_REFERENCE_NUMBER &&
      this.currentSearchFilter !== EReceivingReportFilterBy.PURCHASE_ORDER_ID
    ) {
      this.loadReceivableOrder(order);

      return;
    }

    this.receivingReportFacadeService
      .onGetReceivableOrdersByType({
        type: this.currentSearchFilter,
        receivableId:
          this.currentSearchFilter ===
          EReceivingReportFilterBy.SHIPMENT_REFERENCE_NUMBER
            ? order.supplierPurchaseOrderShipmentId
            : order.label,
        relations: this.relations[this.currentSearchFilter],
      })
      .subscribe({
        next: (order :IReceivableOrder) => {
          this.loadReceivableOrder(order);
        },
        error: (error) => {
          console.error('Error fetching receivables:', error);
        },
      });
  }

  private loadReceivableOrder(order :IReceivableOrder) :void {
    this.currentSearchValue = order?.label?.toString();
    let uniqueItems = order.supplierPurchaseOrderItems.filter((item) => {
      return !this.shipmentItemsFormArray.value.some(
        (value :any) =>
          value.rfqId === item.rfq.id &&
          value.supplierPurchaseOrderId === item.supplierPurchaseOrderId,
      );
    });
    let uniqueLabourItems = order.extraCosts.filter((item) => {
      return !this.shipmentExtraCostsFormArray.value.some(
        (value :any) => value.id === item.id,
      );
    });

    if (
      this.currentSearchFilter ===
      EReceivingReportFilterBy.SHIPMENT_REFERENCE_NUMBER
    ) {
      this.form.patchValue({
        supplierPurchaseOrderShipmentId: order.supplierPurchaseOrderShipmentId,
      });

      uniqueItems = order.supplierPurchaseOrderItems;
      this.shipmentItemsFormArray.clear();

      uniqueLabourItems = order.extraCosts;
      this.shipmentExtraCostsFormArray.clear();

      if (
        order.supplierPurchaseOrderShipment?.warehouseId &&
        order.supplierPurchaseOrderShipment?.warehouse
      ) {
        this.form.patchValue({
          bulkWarehouse: order.supplierPurchaseOrderShipment.warehouse,
        });
      }
    }

    this.initShipmentItemsFormArray({
      ...order,
      supplierPurchaseOrderItems: uniqueItems,
    });
    this.initShipmentExtraCostsFormArray({
      ...order,
      extraCosts: uniqueLabourItems,
    });
  }

  onSelectWarehouse(selectedWarehouse :IWarehouse, idx :number) :void {
    const lineItem = this.shipmentItemsFormArray.at(idx);
    lineItem.patchValue({
      warehouse: selectedWarehouse,
      location: null,
      lot: null,
    });

    this.setBulkUpdateWarehouseValue();
    this.setBulkUpdateLocationValue();
  }

  onSelectBulkUpdateWarehouse(warehouse :IWarehouse) :void {
    this.form.patchValue({
      bulkWarehouse: warehouse,
      bulkLocation: null,
    });

    this.shipmentItemsFormArray.controls.forEach((control) => {
      control.get('warehouse').setValue(warehouse);
      control.get('location').setValue(null);
      control.get('lot').setValue(null);
    });

    this.setBulkUpdateWarehouseValue();
    this.setBulkUpdateLocationValue();
  }

  onSelectLocation(selectedLocation :IWarehouse, idx :number) :void {
    const lineItem = this.shipmentItemsFormArray.at(idx);
    lineItem.patchValue({
      location: selectedLocation,
    });

    this.setBulkUpdateWarehouseValue();
    this.setBulkUpdateLocationValue();
  }

  onSelectBulkUpdateLocation(location :IWarehouse) :void {
    this.form.patchValue({
      bulkLocation: location,
    });

    this.shipmentItemsFormArray.controls.forEach((control) => {
      if (control.get('warehouse').value.id === location.parent_id) {
        control.get('location').setValue(location);
      }
    });
  }

  onLoadLots(shipmentItem :any) :void {
    this.locationFacadeService
      .getRfqLotsIds({
        rfqIds: shipmentItem.rfqId,
        limit: '20',
        page: '1',
        // searchQuery=
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  onSearchLot(searchQuery :string, shipmentItem :any) :void {
    this.locationFacadeService
      .getRfqLotsIds({
        rfqIds: shipmentItem.rfqId,
        limit: '20',
        page: '1',
        searchQuery,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  onSearch(value :string) :void {
    this.currentSearchValue = value;
    this.receivingReportFacadeService
      .onSearchReceivableOrders({
        searchQuery: value,
        type: this.currentSearchFilter,
        relations: this.relations[this.currentSearchFilter],
        supplierId: this.supplierControl?.value?.id ?? null,
      })
      .pipe(
        takeUntil(this.onDestroy$),
        catchError((response) => {
          this.snackbarRef = this.snackbar.open(response.error, 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          return throwError(() => response.error);
        }),
      )
      .subscribe();
  }

  onLoadBulkLocations() :void {
    if (!this.form.get('bulkWarehouse').value || !this.allowRefecthLocations) {
      // Reset the locations
      this.locationFacadeService.updateState({
        locations: [],
      });
      this.locationFacadeService.cancelRequest();

      return;
    }

    this.locationFacadeService.setCurrentQueryParams({
      ...this.currentLocationQueryParams,
      parent_id: this.form.get('bulkWarehouse').value.id.toString(),
    });

    this.allowRefecthLocations = true;
  }

  onSearchParentBulkLocation(value :string) :void {
    this.locationFacadeService.setCurrentQueryParams({
      ...this.currentLocationQueryParams,
      keyword: value,
      parent_id: '0',
    });
  }

  onSearchBulkLocation(value :string) :void {
    if (!this.form.get('bulkWarehouse').value) {
      this.locationFacadeService.updateState({
        locations: [],
      });

      return;
    }

    this.locationFacadeService.setCurrentQueryParams({
      ...this.currentLocationQueryParams,
      keyword: value,
      parent_id: this.form.get('bulkWarehouse').value.id.toString(),
    });
  }

  onSelectBulkWarehouse(event :MatAutocompleteSelectedEvent) :void {
    this.allowRefecthLocations = false;
    const warehouse = event.option.value;
    this.shipmentItemsFormArray.controls.forEach((control) => {
      control.get('warehouse').setValue(warehouse);
      control.get('location').setValue(null);
      control.get('lot').setValue(null);
    });

    this.locationFacadeService.updateState({
      locations: [],
    });
  }

  onSelectBulkLocation(event :MatAutocompleteSelectedEvent) :void {
    const location = event.option.value;
    this.shipmentItemsFormArray.controls.forEach((control) => {
      if (control.get('warehouse').value.id === location.parent_id) {
        control.get('location').setValue(location);
      }
    });

    this.locationFacadeService.updateState({
      locations: [],
    });
  }

  onLoadParentLocations() :void {
    if (this.allowRefecthLocations) {
      this.locationFacadeService
        .getLocationsV1({
          ...this.currentLocationQueryParams,
          parent_id: '0',
          keyword: '',
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe();
    } else {
      this.allowRefecthLocations = true;
    }
  }

  onLoadLocations(shipmentItem :any) :void {
    if (this.allowRefecthLocations && shipmentItem.warehouse?.id) {
      this.locationFacadeService.setCurrentQueryParams({
        ...this.currentLocationQueryParams,
        parent_id: shipmentItem.warehouse.id,
        keyword: '',
      });
    } else {
      this.allowRefecthLocations = true;
    }
  }

  onSearchLocation(value :string) :void {
    this.locationFacadeService.setCurrentQueryParams({
      ...this.currentLocationQueryParams,
      keyword: value,
    });
  }

  onSearchParentLocation(value :string) :void {
    this.locationFacadeService.setCurrentQueryParams({
      ...this.currentLocationQueryParams,
      keyword: value,
      parent_id: '0',
    });
  }

  onParentLocationSelected(shipmentItem :any, index :number) :void {
    this.allowRefecthLocations = false;
    this.locationFacadeService.updateState({
      locations: [],
    });

    // Set bulkLocation to null
    this.form.get('bulkWarehouse').patchValue(null);
    this.form.get('bulkLocation').patchValue(null);
    this.shipmentItemsFormArray.controls[index].get('location').patchValue(null);
    this.shipmentItemsFormArray.controls[index].get('lot').patchValue(null);
  }

  onLocationSelected() :void {
    this.allowRefecthLocations = false;
    this.locationFacadeService.updateState({
      locations: [],
    });

    this.setBulkUpdateWarehouseValue();
    this.setBulkUpdateLocationValue();
  }

  onOpenCreateLotForm(shipmentItem :any, index :number) :void {
    this.dialog
      .open(RfqLotFormComponent, {
        data: {
          rfqId: shipmentItem.rfqId,
          quantityOriginal: 0,
          purchaseOrderId: shipmentItem.supplierPurchaseOrderId,
        },
        width: DialogConfig.sizes.medium,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((lot :IRfqLot) => {
        if (lot) {
          this.shipmentItemsFormArray.controls[index].get('lot').patchValue(lot);
        }
      });
  }

  onRemoveShipmentItem(index :number) :void {
    this.shipmentItemsFormArray.removeAt(index);
  }

  displayFn(option :any) :string {
    return option && option.referenceNumber ? `${option.referenceNumber}` : '';
  }

  displayFnLocation(option :any) :string {
    return option?.location_name || option?.locationName;
  }

  displayFnLot(option :any) :string {
    return option?.lot_number || option?.lotNumber;
  }

  onFilterByChange(event :MatSelectChange) :void {
    this.currentSearchFilter = event.value;
    this.receivableOrders = [];
  }

  onRemoveItem(id :number) :void {
    const index = this.shipmentItemsFormArray.controls.findIndex(
      (control) => control.get('id').value === id,
    );
    this.shipmentItemsFormArray.removeAt(index);
  }

  onSubmit() :void {
    const { value } = this.form;
    const payload = {
      receivedDate: value.receivedDate,
      receivedBy: value.receivedBy,
      receivedAt: value.receivedAt,
      trackingNumber: value.trackingNumber,
      shippedVia: value.trackingNumber,
      totalQuantity: this.totalItemUnits,
      totalCost: this.totalItemCost,
      // supplierPurchaseOrderShipmentId: value.supplierPurchaseOrderShipmentId,
      receivingReportItems: value.shipmentItems.map((item :any) => ({
        id: item?.id,
        supplierPurchaseOrderId: item.supplierPurchaseOrderId,
        supplierPurchaseOrderItemId: item.supplierPurchaseOrderItemId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        lineTotal: item.quantity * item.unitPrice,
        receivedUnitOfMeasureId: item.receivedUnitOfMeasureId,
        locationId: item.location?.id,
        lotId: item.lot?.id,
        warehouseId: item.warehouse?.id,
        tags: item.tagIds,
        rfqId: item.rfqId,
        supplierPurchaseOrderShipmentId: item.supplierPurchaseOrderShipmentId,
      })),
      receivingReportExtraCosts: value.shipmentExtraCosts.map((cost :any) => ({
        id: cost?.id || 0,
        name: cost.name,
        quantity: cost.quantity,
        unitPrice: cost.unitPrice,
        costAllocationType: cost?.costAllocationType,
        categoryIds: cost.categories?.map((category) => category.id),
      })),
      alternateIds: value.alternateIds,
    };

    if (this.currentReceivingReportId) {
      this.receivingReportFacadeService
        .updateReceiveReportShipment(this.currentReceivingReportId, payload)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response) => {
            this.snackbar.open('Receiving report updated', 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.dialogRef.close(response);
          },
          error: (response) => {
            this.snackbar.open(
              response?.error || 'Error updating receiving report',
              'Error',
            );
          },
        });
    } else {
      this.receivingReportFacadeService
        .createReceivingReportShipment(payload)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response) => {
            this.snackbar.open('Receiving report created', 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.dialogRef.close(response);
          },
          error: (response) => {
            this.snackbar.open(
              response?.error || 'Error creating receiving report',
              'Error',
            );
          },
        });
    }
  }

  onSearchSupplierChange(keyword :string) :void {
    this.onSelectSupplier(null);
    if (keyword === '') {
      this.suppliers = this.suppliersPreviousList;
    } else {
      const lowerCaseKeyword = keyword.toLowerCase();
      this.suppliers = this.suppliers.filter((item :any) =>
        item.factory_name.toLowerCase().includes(lowerCaseKeyword),
      );
    }
  }

  onSelectSupplier(supplier :any) :void {
    if (!supplier) {
      this.supplierControl.setValue(null);
      this.onSearch(this.currentSearchValue);

      return;
    }

    if (this.supplierControl.value && this.supplierControl.value === supplier.id) {
      return;
    }

    this.supplierIdDisplayValue = supplier.factory_name ?? null;
    this.supplierControl.setValue(supplier);
    this.onSearch(this.currentSearchValue);
  }

  private initTrackInventoryByLocation() :void {
    this.settingService.trackInventoryByLocationValue$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((enabled :boolean) => {
        this.ftLocationBins = enabled;
      });
  }

  private loadCurrentReceivingReport() :void {
    const relations = [
      'receivingReportItems',
      'receivingReportItems.stock.rfq',
      'receivingReportItems.tags',
      'receivingReportExtraCosts.categories.productCategory',
      'receivingReportItems.receivedUnitOfMeasure',
      'receivingReportItems.location',
      'receivingReportItems.lot',
      'receivingReportItems.stock.warehouse',
      'receivingReportItems.supplierPurchaseOrderItem.rfq',
      'receivingReportItems.supplierPurchaseOrderShipment.shipmentItems.supplierPurchaseOrderItem.rfq',
      'receivingReportItems.supplierPurchaseOrderShipment.shipmentItems.supplierPurchaseOrderItem.lot',
      'receivingReportItems.supplierPurchaseOrderShipment.shipmentItems.unitOfMeasure',
      'alternateIds',
    ];
    this.receivingReportFacadeService
      .getReceivingReportById({
        id: this.currentReceivingReportId,
        params: {
          relations: relations.join('|'),
          responseFormat: ResponseFormatConstant.FORMDATA,
        },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.initForm(response);
          if (response.receivingReportItems.length) {
            const targetWarehouseId =
              response.receivingReportItems[0]?.warehouse?.id;
            const targetingLocationId =
              response.receivingReportItems[0]?.locationId;
            const isSameWarehouse = response.receivingReportItems.every(
              (item :any) => item.warehouseId === targetWarehouseId,
            );
            const isSameLocation = response.receivingReportItems.every(
              (item :any) => item.locationId === targetingLocationId,
            );

            if (isSameWarehouse) {
              this.form
                .get('bulkWarehouse')
                .patchValue(response.receivingReportItems[0]?.warehouse);
            }

            if (isSameLocation) {
              this.form
                .get('bulkLocation')
                .patchValue(response.receivingReportItems[0]?.location);
            }
          }
        },
        error: (response) => {
          this.snackbar.open(
            response?.error || 'Error loading receiving report',
            'Error',
          );
        },
      });
  }

  private initForm(report ?:any) :void {
    const receivedDate = report
      ? this.datePipe.transform(report.receivedDate, 'yyyy-MM-dd')
      : new Date().toISOString();
    const shipmentExtraCosts =
      report?.receivingReportExtraCosts || this.data?.extraCosts || [];

    this.form = this.fb.group(
      {
        receivedDate: [receivedDate, Validators.required],
        trackingNumber: [report?.trackingNumber || ''],
        receivedBy: [report?.receivedBy || '', Validators.required],
        receivedAt: [report?.receivedAt || '', Validators.required],
        bulkWarehouse: [null],
        bulkLocation: [{ value: null, disabled: true }],
        supplierPurchaseOrderShipmentId: [report?.id || null],
        shipmentItems: report
          ? this.fb.array(
              report?.receivingReportItems?.map((item :any) => {
                return this.createItemGroup({ report, item });
              }),
            )
          : this.fb.array([]),
        shipmentExtraCosts: shipmentExtraCosts
          ? this.fb.array(
              shipmentExtraCosts?.map((cost) =>
                this.fb.group({
                  id: [cost.id || 0],
                  name: [cost.name],
                  quantity: [cost.quantity],
                  unitPrice: [cost.unitPrice],
                  costAllocationType: [cost?.costAllocationType || null],
                  categories: cost?.categories?.length
                    ? this.fb.array(
                        cost?.categories?.map((category) => ({
                          id: category?.productCategoryId,
                          name: category?.productCategory?.name,
                        })),
                      )
                    : this.fb.array([]),
                }),
              ),
            )
          : this.fb.array([]),
        supplier: [null],
        alternateIds: report?.alternateIds 
          ? this.fb.array(report.alternateIds.map((altId: any) => this.fb.control(altId.alternateId)))
          : this.fb.array([]),
      },
      { validators: atLeastOneArrayItemValidator('shipmentItems') },
    );

    // Initialize alternateIdsFormArray
    this.alternateIdsFormArray = this.form.get('alternateIds') as UntypedFormArray;

    if (this.data?.preloadedLineItems && this.data?.preloadedLineItems.length) {
      this.initPreloadedLineItemsFormArray(this.data.preloadedLineItems);
    }

    // Listen to valueChanges and update the subject
    this.form
      .get('shipmentItems')!
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((test) => {
        this.shipmentItemsSubject.next(this.shipmentItemsFormArray.controls);
      });

    this.form.get('shipmentItems')?.updateValueAndValidity({ emitEvent: true });
  }

  private initData() :void {
    this.ftLocationBins = this?.data?.ftLocationBins || false;
  }

  private createItemGroup({ report, item }) :UntypedFormGroup {
    const receivingReportItem = report.receivingReportItems.find(
      (reportItem :any) =>
        reportItem.supplierPurchaseOrderItemId === item.supplierPurchaseOrderItemId,
    );
    let itemGroup :UntypedFormGroup;

    if (receivingReportItem) {
      itemGroup = this.fb.group({
        id: [receivingReportItem.id || 0],
        supplierPurchaseOrderItemId: [
          receivingReportItem.supplierPurchaseOrderItemId,
        ],
        rfqId: [receivingReportItem?.rfqId],
        supplierPurchaseOrderId: [receivingReportItem?.supplierPurchaseOrderId],
        sku: [receivingReportItem?.sku],
        productName: [receivingReportItem?.productName],
        warehouse: [receivingReportItem?.warehouse],
        location: [receivingReportItem?.location],
        lot: [receivingReportItem?.lot],
        quantity: [
          receivingReportItem.quantity,
          [Validators.required, requiredNonZeroValidator()],
        ],
        unitOfMeasure: [receivingReportItem.unitOfMeasure],
        unitPrice: [receivingReportItem?.unitPrice, [Validators.required]],
        tagIds: this.fb.array(receivingReportItem?.tagIds ?? []),
        lotTracking: [receivingReportItem?.lotTracking],
        receivedUnitOfMeasureId: [receivingReportItem?.receivedUnitOfMeasureId],
        productCategoryId: [receivingReportItem?.productCategoryId],
        productCategory: [receivingReportItem.productCategory],
      });
    } else {
      itemGroup = this.fb.group({
        id: [item.id || 0],
        supplierPurchaseOrderItemId: [item.supplierPurchaseOrderItemId],
        rfqId: [item?.supplierPurchaseOrderItem?.rfq?.id],
        supplierPurchaseOrderId: [item.supplierPurchaseOrderId],
        sku: [item?.supplierPurchaseOrderItem?.rfq?.internalSku],
        productName: [item?.supplierPurchaseOrderItem?.rfq?.productName],
        warehouse: [null],
        location: [null],
        lot: [item?.supplierPurchaseOrderItem?.lot || null],
        quantity: [item.quantity],
        unitOfMeasure: [item.unitOfMeasure.name],
        unitPrice: [item?.supplierPurchaseOrderItem?.unitPrice],
        tagIds: this.fb.array([]),
        lotTracking: [item?.supplierPurchaseOrderItem?.rfq?.lotTracking],
        receivedUnitOfMeasureId: [item.unitOfMeasureId],
        productCategoryId: [item?.stock?.rfq?.productCategoryId],
        productCategory: [item?.stock?.rfq?.productCategory],
      });
    }

    return itemGroup;
  }

  private initPreloadedLineItemsFormArray(lineItemsArray :any[]) :void {
    lineItemsArray.forEach((item :any) => {
      const itemGroup = this.fb.group({
        id: [0],
        supplierPurchaseOrderItemId: [item.id],
        rfqId: [item?.rfq?.id],
        supplierPurchaseOrderId: [item.purchaseOrder.id],
        sku: [item.internalSku],
        productName: [item.productName],
        warehouse: [item?.warehouse],
        location: [item?.location],
        lot: [item.lot],
        quantity: [
          item.addToReceiveQty,
          [Validators.required, requiredNonZeroValidator()],
        ],
        unitOfMeasure: [item.unit],
        unitPrice: [item.unitPrice, [Validators.required]],
        tagIds: this.fb.array([]),
        lotTracking: [item?.rfq?.lotTracking],
        receivedUnitOfMeasureId: [item.orderedUomId],
        supplierPurchaseOrderShipmentId: [],
        productCategoryId: [item?.rfq?.productCategoryId],
        productCategory: [item?.rfq?.productCategory],
      });
      this.shipmentItemsFormArray.push(itemGroup);
    });
  }

  private initShipmentItemsFormArray(order :IReceivableOrder) :void {
    const isPOFromTO = this.isInternalTransferOrder(order);
    order.supplierPurchaseOrderItems.forEach((item) => {
      const quantity =
        this.currentSearchFilter ===
        EReceivingReportFilterBy.SHIPMENT_REFERENCE_NUMBER
          ? item?.shipmentQuantity
          : Number(item.quantity || 0) - Number(item?.receivedEntry?.quantity || 0);
      const itemGroup = this.fb.group({
        id: [item?.id || 0],
        supplierPurchaseOrderItemId: [item.id],
        rfqId: [item?.rfq?.id],
        supplierPurchaseOrderId: [item.supplierPurchaseOrderId],
        sku: [item.internalSku],
        productName: [item.productName],
        warehouse: [this.getWarehouse(item, order, isPOFromTO)],
        location: [this.getLocation(item, order, isPOFromTO)],
        lot: [null],
        quantity: [quantity, [Validators.required, requiredNonZeroValidator()]],
        unitOfMeasure: [item.receivableUnitOfMeasure.name],
        unitPrice: [item.unitPrice, [Validators.required]],
        tagIds: this.fb.array([]),
        lotTracking: [item?.rfq?.lotTracking],
        receivedUnitOfMeasureId: [item.orderedUomId],
        supplierPurchaseOrderShipmentId: [
          order.supplierPurchaseOrderShipmentId || null,
        ],
        productCategoryId: [item?.rfq?.productCategoryId],
        productCategory: [item?.rfq?.productCategory],
      });

      this.shipmentItemsFormArray.push(itemGroup);
    });

    this.form.updateValueAndValidity({ emitEvent: true });
  }

  private isInternalTransferOrder(order :IReceivableOrder) :boolean {
    return (
      order?.supplierPurchaseOrder?.supplierOrderType ===
      this.eOrderTypeValue.INTERNAL_TRANSFER_ORDER
    );
  }

  private getWarehouse(
    item :OrderItem,
    order :IReceivableOrder,
    isPOFromTO :boolean,
  ) :any {
    // First check if there's a warehouse associated with the shipment
    if (order?.supplierPurchaseOrderShipment?.warehouseId) {
      return order?.supplierPurchaseOrderShipment?.warehouse;
    }

    if (isPOFromTO) {
      return (
        order?.supplierPurchaseOrder?.internalResource?.destinationWarehouse ||
        item.supplierPurchaseOrder?.internalResource?.destinationWarehouse
      );
    }

    return item.stock.warehouse;
  }

  private getLocation(
    item :OrderItem,
    order :IReceivableOrder,
    isPOFromTO :boolean,
  ) :any {
    // If we have a shipment with a warehouse, use its default receiving location
    if (
      order?.supplierPurchaseOrderShipment?.warehouseId &&
      order?.supplierPurchaseOrderShipment?.warehouse?.defaultReceivingLocation
    ) {
      return order.supplierPurchaseOrderShipment.warehouse.defaultReceivingLocation;
    }

    if (isPOFromTO) {
      return (
        order?.supplierPurchaseOrder?.internalResource?.destinationWarehouse
          ?.defaultReceivingLocation ||
        item.supplierPurchaseOrder?.internalResource?.destinationWarehouse
          ?.defaultReceivingLocation
      );
    }

    return (
      item?.rfq?.defaultReceivingBin ??
      item.stock?.warehouse?.defaultReceivingLocation
    );
  }

  private addShippingCostToExtraCosts(order :IReceivableOrder): void {
    if (order.supplierPurchaseOrder?.internalResource?.shippingCost && !this.currentReceivingReportId) {
      const shippingCostExists = this.shipmentExtraCostsFormArray.value.some(
        (cost) => cost.name === EExtraCosts.SHIPPING_COST
      );
      if (!shippingCostExists) {
        this.shipmentExtraCostsFormArray.push(
          this.fb.group({
            referenceNumber: [
              order.supplierPurchaseOrderShipmentId ? order.label : '',
            ],
            id: [0],
            name: [EExtraCosts.SHIPPING_COST],
            quantity: [1],
            unitPrice: [Number(order.supplierPurchaseOrder.internalResource.shippingCost)],
            costAllocationType: [CostAllocationTypeConstants.UNIT_BASED_ALLOCATION],
            categories: this.fb.array([]),
          }),
        );
      }
    }
  }

  private initShipmentExtraCostsFormArray(order :IReceivableOrder) :void {
    order.extraCosts.forEach((cost) => {
      this.shipmentExtraCostsFormArray.push(
        this.fb.group({
          referenceNumber: [
            order.supplierPurchaseOrderShipmentId ? order.label : '',
          ],
          id: [cost.id || 0],
          name: [cost.name],
          quantity: [cost.quantity],
          unitPrice: [cost.unitPrice],
          costAllocationType: [cost.costAllocationType ?? null],
          categories: cost?.categories?.length
            ? this.fb.array(
                cost?.categories?.map((category) => ({
                  id: category?.productCategoryId,
                  name: category?.productCategory?.name,
                })),
              )
            : this.fb.array([]),
        }),
      );
    });

    // Add shipping cost to extra costs if it exists
    this.addShippingCostToExtraCosts(order);
    this.form.updateValueAndValidity();
  }

  private getReceivableOrders() :Observable<IReceivableOrder> {
    return this.receivingReportFacadeService.receivablesOrder$.pipe(
      skip(1),
      takeUntil(this.onDestroy$),
      tap((receivableOrder) => {
        if (receivableOrder?.length) {
          if (this.snackbarRef) {
            this.snackbarRef.dismiss();
          }
          this.receivableOrders = [...receivableOrder];
        } else if (receivableOrder?.length <= 0) {
          this.receivableOrders = [];
          this.snackbarRef = this.snackbar.open('No Items found', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        }
      }),
    );
  }

  private getSuppliers() :void {
    this.loadingSupplier = true;
    this.supplierApiService
      .get({})
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.loadingSupplier = false)),
      )
      .subscribe({
        next: (response) => {
          const { records } = response;
          this.suppliers = records;
          this.suppliersPreviousList = records;
        },
      });
  }

  private setBulkUpdateWarehouseValue() :void {
    const items :UntypedFormArray = this.shipmentItemsFormArray;
    if (items?.length > 0) {
      const warehouseIds = items.controls.map((item) => item.value.warehouse?.id);

      // Check for undefined in warehouseIds
      if (warehouseIds.some((id) => id === undefined)) {
        this.form.patchValue({ bulkWarehouse: null });
        this.toggleBulkLocationField();

        return;
      }

      const allEqual = warehouseIds.every(
        (warehouseId) => warehouseId === warehouseIds[0],
      );

      this.form.patchValue({
        bulkWarehouse: allEqual ? items.at(0).value.warehouse : null,
      });
    }

    this.toggleBulkLocationField();
  }

  private setBulkUpdateLocationValue() :void {
    const items :UntypedFormArray = this.shipmentItemsFormArray;
    if (items?.length > 0) {
      const locationIds = items.controls.map((item) => item.value.location?.id);

      // Check for undefined in locationIds
      if (locationIds.some((id) => id === undefined)) {
        this.form.patchValue({ bulkLocation: null });

        return;
      }

      const allEqual = locationIds.every(
        (locationId) => locationId === locationIds[0],
      );

      this.form.patchValue({
        bulkLocation: allEqual ? items.at(0).value.location : null,
      });
    }
  }

  private toggleBulkLocationField() :void {
    if (
      !this.form.value.bulkWarehouse?.id ||
      this.form.value.bulkWarehouse?.id <= 0
    ) {
      this.form.get('bulkLocation').disable();

      return;
    }

    this.form.get('bulkLocation').enable();
  }

  get currentReceivingReportId() :number | null {
    return this.data?.id || null;
  }

  get shipmentItemsFormArray() :UntypedFormArray {
    return this.form.get('shipmentItems') as UntypedFormArray;
  }

  get shipmentExtraCostsFormArray() :UntypedFormArray {
    return this.form.get('shipmentExtraCosts') as UntypedFormArray;
  }

  get receivedByControl() :UntypedFormControl {
    return this.form.get('receivedBy') as UntypedFormControl;
  }

  get receivedAtControl() :UntypedFormControl {
    return this.form.get('receivedAt') as UntypedFormControl;
  }

  get currentQueryParams() :any {
    return this.purchaseOrderFacadeService.currentShipmentQueryParams;
  }

  get loadingReceivableOrders$() :Observable<boolean> {
    return this.receivingReportFacadeService.loadingReceivableOrders$;
  }

  get shipments$() :Observable<IPurchaseOrderShipment[]> {
    return this.purchaseOrderFacadeService.shipments$;
  }

  get loadingCurrentReceivingReport$() :Observable<boolean> {
    return this.receivingReportFacadeService.loading$;
  }

  get loadingLocations$() :Observable<boolean> {
    return this.locationFacadeService.loading$;
  }

  get locations$() :Observable<ILocationOld[]> {
    return this.locationFacadeService.locations$;
  }

  get lots$() :Observable<IRfqLot[]> {
    return this.locationFacadeService.lots$;
  }

  get submitting$() :Observable<boolean> {
    return this.receivingReportFacadeService.submitting$;
  }

  get receivablesOrders$() :Observable<IReceivableOrder> {
    return this.receivingReportFacadeService.receivablesOrder$;
  }

  get currentLocationQueryParams() :{ [param :string] :string | string[] } {
    return this.locationFacadeService.currentQueryParams;
  }

  get totalItemUnits() :number {
    return this.shipmentItemsFormArray.controls.reduce((acc, control) => {
      return acc + Number(control.value.quantity);
    }, 0);
  }

  get totalItemCost() :number {
    return this.shipmentItemsFormArray.controls.reduce((acc, control) => {
      return acc + Number(control.value.quantity) * Number(control.value.unitPrice);
    }, 0);
  }

  get overAllTotalCost() :number {
    // return total item cost + total extra cost
    return (
      this.totalItemCost +
      this.shipmentExtraCostsFormArray.controls.reduce((acc, control) => {
        return (
          acc + Number(control.value.quantity) * Number(control.value.unitPrice)
        );
      }, 0)
    );
  }

  get supplierControl() :UntypedFormControl {
    return this.form.get('supplier') as UntypedFormControl;
  }
}
